import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { ContactForm } from "@/components/sections/ContactForm";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import {
  ArrowRight,
  Check,
  CalendarCheck,
  ClipboardList,
  ShieldCheck,
  TrendingUp,
  FileSignature,
  FolderArchive,
  Lightbulb,
  Users,
  Plus,
  Minus,
  Building2,
  Briefcase,
  Rocket,
  Truck,
  Store,
  Phone,
  Quote,
  Sparkles,
  CalendarRange,
  FileCheck2,
  PieChart,
} from "lucide-react";
import hero from "@/assets/hero-bokslut-dark.webp";
import faqImage from "@/assets/faq-bokslut.webp";
import advisoryImage from "@/assets/boksult-2.webp";
import storyDigital from "@/assets/boksult-3.webp";
import storyWorkflow from "@/assets/boksult-4.webp";
import storySupport from "@/assets/boksult-1.webp";
import { StoryAlternating } from "@/components/sections/StoryAlternating";
import logoFortnox from "@/assets/logo-fortnox.webp";
import logoVisma from "@/assets/logo-visma.webp";
import logoHogia from "@/assets/logo-hogia.webp";
import logoKivra from "@/assets/logo-kivra.webp";
import logoPleo from "@/assets/logo-pleo.webp";

const trustItems = [
  "Auktoriserad bokslutskompetens",
  "Inga missade deadlines",
  "Skatteoptimerat resultat",
  "Digitalt bokslut & e-signering",
];

const included = [
  {
    icon: FileCheck2,
    title: "Komplett årsbokslut & årsredovisning",
    body: "Vi upprättar årsbokslut, årsredovisning enligt K2 eller K3 och ser till att alla periodiseringar, avskrivningar och bokslutsdispositioner blir korrekta — utan eftersläpningar.",
  },
  {
    icon: FileSignature,
    title: "Inkomstdeklaration för bolag & ägare",
    body: "Vi sköter inkomstdeklaration 2 för aktiebolag samt K10-blanketten för dig som delägare. Du slipper grubbla över 3:12-reglerna och vad som ska in var.",
  },
  {
    icon: TrendingUp,
    title: "Skatteplanering inför bokslut",
    body: "Vi går igenom resultatet i god tid och föreslår konkreta åtgärder — periodiseringsfond, utdelning, tjänstepension eller investeringar — så du betalar rätt skatt, inte för mycket.",
  },
  {
    icon: CalendarCheck,
    title: "Vi håller koll på alla datum",
    body: "Inlämning till Bolagsverket, Skatteverket och bolagsstämma — vi påminner i god tid och säkerställer att inget hamnar mellan stolarna. Du behöver aldrig oroa dig för förseningsavgifter.",
  },
  {
    icon: ClipboardList,
    title: "Tydliga checklistor i hela processen",
    body: "Du ser alltid var i bokslutsarbetet vi befinner oss och vilka underlag som saknas. Inga utdragna processer, inga obesvarade frågor.",
  },
  {
    icon: ShieldCheck,
    title: "Trygg granskning före signering",
    body: "Innan något lämnas in granskas bokslutet av en andra par ögon. Vi kvalitetssäkrar siffrorna så att du kan signera med full trygghet — digitalt eller på papper.",
  },
];

const steps = [
  {
    icon: FolderArchive,
    title: "Vi samlar in & stämmer av underlagen",
    body: "Eftersom vi arbetar digitalt under året finns merparten av underlagen redan på plats. Vi efterfrågar resterande dokument i god tid — lager, kundfordringar, leverantörsskulder och eventuella avtal.",
  },
  {
    icon: PieChart,
    title: "Vi upprättar bokslut & föreslår skatteåtgärder",
    body: "Vi gör periodiseringar, avskrivningar och bokslutsdispositioner. Du får ett resultatförslag tillsammans med konkreta rekommendationer för skatteoptimering — innan bokslutet låses.",
  },
  {
    icon: FileSignature,
    title: "Du signerar — vi lämnar in",
    body: "Årsredovisning och deklaration signeras digitalt med BankID eller på papper om du föredrar det. Vi lämnar in till Bolagsverket och Skatteverket inom utsatt tid.",
  },
];

const forWhom = [
  { icon: Briefcase, label: "Enskild firma" },
  { icon: Building2, label: "Aktiebolag (AB)" },
  { icon: TrendingUp, label: "Tillväxtbolag" },
  { icon: Rocket, label: "Startup & scale-up" },
  { icon: Store, label: "Handels- & tjänsteföretag" },
  { icon: Truck, label: "Bygg, transport & fastighet" },
];

const why = [
  {
    title: "Proaktivt — inte reaktivt",
    body: "Vi väntar inte tills räkenskapsåret är slut. Vi följer dina siffror löpande och föreslår åtgärder medan det fortfarande är möjligt att påverka resultatet.",
  },
  {
    title: "Skatteoptimerat resultat",
    body: "Genom skatteplanering inför bokslut säkerställer vi att du utnyttjar lagliga avdrag, periodiseringsfonder och utdelningsutrymme — utan att tumma på regelverket.",
  },
  {
    title: "Inga sena nätter, inga deadlines att jaga",
    body: "Vi äger processen från första underlag till inlämnad deklaration. Du fokuserar på verksamheten — vi säkerställer att allt är inne i tid.",
  },
  {
    title: "K2 eller K3 — vi väljer rätt regelverk",
    body: "Vi vägleder dig till rätt regelverk baserat på bolagets storlek, ägarstruktur och framtidsplaner. Rätt val nu sparar tid och skatt senare.",
  },
  {
    title: "Bokslut som beslutsunderlag",
    body: "Bokslutet är inte bara en lagstadgad rapport — det är en strategisk avstämning. Vi hjälper dig läsa siffrorna och förstå vad de säger om nästa år.",
  },
  {
    title: "Digitalt eller fysiskt — du väljer",
    body: "Föredrar du e-signering och Teams-möte? Eller papper och en kopp kaffe på kontoret? Båda fungerar lika bra hos oss. Vi anpassar oss efter dig.",
  },
];

const faqs = [
  {
    q: "Vad är skillnaden mellan årsbokslut och årsredovisning?",
    a: "Årsbokslut är en sammanställning av räkenskapsåret som alla bokföringsskyldiga företag ska upprätta. Årsredovisning är en mer omfattande rapport som aktiebolag, ekonomiska föreningar och vissa större företag måste lämna in till Bolagsverket. Den innehåller förvaltningsberättelse, resultaträkning, balansräkning, noter och i vissa fall kassaflödesanalys. Vi upprättar båda — och säkerställer att de följer K2 eller K3-regelverket.",
  },
  {
    q: "När måste bokslut och deklaration vara klart?",
    a: "Aktiebolag ska lämna in årsredovisning till Bolagsverket senast sju månader efter räkenskapsårets slut. Inkomstdeklaration 2 ska in till Skatteverket inom samma tidsram (med möjlighet till anstånd). För enskild firma gäller andra datum. Hos oss behöver du inte hålla koll — vi påminner i god tid och säkerställer att allt lämnas in innan deadline, så du undviker förseningsavgifter.",
  },
  {
    q: "Vad kostar bokslut och deklaration?",
    a: "Priset beror på bolagsform, transaktionsvolym, regelverk (K2 eller K3) och hur strukturerad din löpande bokföring är. Företag som har sin löpande bokföring hos oss får ett mer förutsägbart pris eftersom underlagen redan är digitala och avstämda. Boka ett kostnadsfritt möte så ger vi dig ett konkret förslag baserat på just ditt bolag.",
  },
  {
    q: "Kan ni göra bokslut även om jag har bokföringen någon annanstans?",
    a: "Ja. Vi tar gärna emot bokslutsuppdrag även för företag där vi inte sköter den löpande bokföringen. Vi går igenom huvudboken, stämmer av konton, gör nödvändiga justeringar och upprättar årsredovisning och deklaration. Många väljer att flytta över även den löpande bokföringen efteråt — men det är inget krav.",
  },
  {
    q: "Hjälper ni till med skatteplanering inför bokslut?",
    a: "Absolut — det är en kärndel av vårt erbjudande. Vi går igenom resultatet före årsskiftet och föreslår åtgärder som periodiseringsfond, utdelning enligt 3:12, tjänstepension, investeringar i inventarier och andra lagliga sätt att optimera skatten. Skatteplanering ingår i vårt bokslutspaket — du får inte en extra faktura för rådgivningen.",
  },
  {
    q: "Måste jag komma till kontoret för att signera?",
    a: "Nej. Vi erbjuder digital signering med BankID, vilket gör hela processen smidig — du kan signera årsredovisning och deklaration var du än befinner dig. Föredrar du att träffas fysiskt och skriva under på papper? Det går alldeles utmärkt också. Du väljer det som passar dig bäst.",
  },
];

const partnerLogos = [
  { src: logoFortnox, alt: "Fortnox" },
  { src: logoVisma, alt: "Visma" },
  { src: logoHogia, alt: "Hogia" },
  { src: logoKivra, alt: "Kivra" },
  { src: logoPleo, alt: "Pleo" },
];

const Bokslut = () => {
  const [openFaq, setOpenFaq] = useState<number>(0);

  return (
    <>
      <SEO
        title="Bokslut & deklaration för aktiebolag — Umeå & Stockholm | Cloud Redovisning"
        description="Komplett bokslut, årsredovisning (K2/K3) och inkomstdeklaration. Vi sköter skatteplanering, deadlines och inlämning till Bolagsverket — digitalt och proaktivt. Boka kostnadsfritt möte."
      />

      {/* ───────── HERO (dark) ───────── */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-black text-background">
        <div className="absolute inset-0 -z-10">
          <img
            src={hero}
            alt=""
            className="h-full w-full object-cover opacity-40 animate-slow-zoom"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/90 to-foreground/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-foreground/60" />
        </div>
        <div className="absolute -top-32 -right-32 -z-10 h-[480px] w-[480px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-primary-glow/10 blur-3xl" />

        <div className="container-page">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-primary-glow animate-fade-up backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" /> Tjänst — Bokslut &
                deklaration
              </div>
              <h1 className="display-xl mt-8 text-background animate-fade-up delay-100 text-balance">
                Bokslut & deklaration —{" "}
                <span className="italic text-primary-glow">
                  utan stress, med skatten i balans
                </span>
                .
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-[1.7] font-light text-background/75 animate-fade-up delay-200">
                Vi upprättar årsbokslut, årsredovisning och inkomstdeklaration
                för företag i Umeå, Stockholm och digitalt i hela Sverige.
                Proaktivt, tryggt och med skatteplanering som hjälper dig fatta
                rätt beslut före deadline.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-300">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-background hover:text-foreground shadow-elevated"
                >
                  Boka kostnadsfritt möte <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#hur-det-fungerar"
                  className="inline-flex items-center gap-3 rounded-full border border-background/30 bg-background/5 backdrop-blur px-7 py-3.5 text-sm font-medium text-background transition-all hover:bg-background hover:text-foreground"
                >
                  Se vår bokslutsprocess
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 animate-fade-in delay-200">
              <div className="relative">
                <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-elevated ring-1 ring-background/10">
                  <img
                    src={hero}
                    alt="Digitalt bokslut och deklaration"
                    className="h-full w-full object-cover animate-slow-zoom"
                    fetchpriority="high"
                  />
                </div>

                <div className="absolute -left-4 md:-left-10 top-10 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    K2 / K3
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Rätt regelverk för ditt bolag
                  </div>
                </div>
                <div className="absolute -right-4 md:-right-8 bottom-12 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    0 sek
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    I förseningsavgifter sedan start
                  </div>
                </div>
                <div className="absolute -right-2 md:-right-4 -top-4 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[190px]">
                  <div className="font-display text-2xl text-primary-glow">
                    BankID
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Digital signering — var du än är
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── TRUST BAR ───────── */}
      <section className="bg-foreground text-background py-6">
        <div className="container-page">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {trustItems.map((t) => (
              <div
                key={t}
                className="inline-flex items-center gap-2 text-sm text-background/75"
              >
                <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── WHAT'S INCLUDED ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Vad ingår</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Allt som krävs för ett{" "}
              <span className="italic text-primary">
                korrekt och skatteoptimerat bokslut
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Bokslut handlar om mer än att stänga böckerna. Hos oss ingår
              skatteplanering, deadline-bevakning och rådgivning som faktiskt
              påverkar resultatet.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {included.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-elevated hover:-translate-y-1"
                >
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-medium text-foreground font-display mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-muted leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── STORY — narrative pillars from original site ───────── */}
      <StoryAlternating
        background="elevated"
        sectionEyebrow="Så blir bokslutet smidigt"
        sectionTitle={
          <>
            Tre principer som{" "}
            <span className="italic text-primary">tar bort stressen</span> ur
            bokslutsarbetet.
          </>
        }
        sectionLead="Digitalt arbetssätt, proaktiv planering och ett nära stöd genom hela året — så att bokslut och deklaration blir en avstämning, inte en kris."
        items={[
          {
            eyebrow: "01 — Digitalisering",
            title: "Digitaliseringen förenklar bokslutet",
            body: "Eftersom underlaget redan är inlagt digitalt under året finns allt lättillgängligt inför bokslut och deklaration. Inga sena nattliga jakter på kvitton och avtal. Föredrar du ett fysiskt möte och vill skriva under på papper går det självklart också bra — det är du som väljer.",
            image: storyDigital,
          },
          {
            eyebrow: "02 — Arbetsflöde",
            title: "Bra arbetsflöde — utan utdragna processer",
            body: "Vi arbetar för att få ett så smidigt flöde som möjligt i bokslutsarbetet. Det gör vi dels genom att arbeta digitalt, dels genom att proaktivt och i god tid efterfråga det underlag som behövs. Du behöver inte hålla rätt på några viktiga datum — det gör vi åt dig. Tydliga checklistor visar var i processen vi är, så ingenting hamnar mellan stolarna.",
            image: storyWorkflow,
          },
          {
            eyebrow: "03 — Stöd",
            title: "Ett tryggt stöd till dig som företagare",
            body: "Vi är ett stöd för dig som företagare och arbetar proaktivt nära våra kunder, inte minst vid skatteplanering. Du kan lita på att vi har kompetensen att se vilka åtgärder som kan vidtas — och att vi förklarar dem på ett sätt som gör att du själv förstår vad en bra planering innebär för just ditt bolag.",
            image: storySupport,
          },
        ]}
      />

      {/* ───────── HOW IT WORKS ───────── */}
      <section
        id="hur-det-fungerar"
        className="py-20 md:py-32 bg-background-deep"
      >
        <div className="container-page">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="eyebrow">— Bokslutsprocessen</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Tre steg från räkenskapsår{" "}
                <span className="italic text-primary">
                  till inlämnad deklaration
                </span>
                .
              </h2>
              <p className="lead mt-6">
                Ett tydligt arbetsflöde med checklistor — du vet alltid var i
                processen vi är, och vad nästa steg innebär.
              </p>

              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft">
                <img
                  src={advisoryImage}
                  alt="Cloud Redovisning genomgår bokslutet med kund"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              <Link
                to="/kontakt-umea"
                className="inline-flex items-center gap-3 mt-10 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-foreground"
              >
                Starta ert bokslut <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-5">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.title}
                      className="group relative rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-soft"
                    >
                      <div className="flex items-start gap-6">
                        <div className="relative flex-shrink-0">
                          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-primary-foreground shadow-soft">
                            <Icon className="h-7 w-7" strokeWidth={1.75} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-foreground font-display">
                            {s.title}
                          </h3>
                          <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                            {s.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── DEADLINES STRIP ───────── */}
      <section className="py-16 md:py-20 bg-background border-y border-border">
        <div className="container-page">
          <div className="grid gap-10 md:grid-cols-12 items-center">
            <div className="md:col-span-5">
              <div className="eyebrow">— Inga deadlines att hålla koll på</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Vi <span className="italic text-primary">bevakar datumen</span>{" "}
                — du fokuserar på företaget.
              </h2>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
              {[
                {
                  icon: CalendarRange,
                  label: "Bolagsstämma",
                  body: "Inom 6 mån efter räkenskapsår",
                },
                {
                  icon: FileCheck2,
                  label: "Bolagsverket",
                  body: "Årsredovisning inom 7 mån",
                },
                {
                  icon: FileSignature,
                  label: "Skatteverket",
                  body: "Inkomstdeklaration 2 i tid",
                },
              ].map((d) => {
                const Icon = d.icon;
                return (
                  <div
                    key={d.label}
                    className="group rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-soft"
                  >
                    <Icon
                      className="h-6 w-6 text-primary mb-3"
                      strokeWidth={1.75}
                    />
                    <div className="font-display text-base text-foreground">
                      {d.label}
                    </div>
                    <div className="text-xs text-foreground-muted mt-1.5 leading-snug">
                      {d.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FOR WHOM ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow">— Vi gör bokslut för</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Bokslut & deklaration för{" "}
              <span className="italic text-primary">alla typer av företag</span>
              .
            </h2>
            <p className="lead mt-6">
              Från enskild firma till växande aktiebolag — vi anpassar
              regelverk, omfattning och rådgivning efter ditt bolags storlek och
              fas.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {forWhom.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="flex items-center gap-4 rounded-2xl border border-border bg-card px-6 py-5 transition-all hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── WHY CLOUD ───────── */}
      <section className="py-20 md:py-32 bg-background-deep">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Varför Cloud Redovisning</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Bokslut som{" "}
              <span className="italic text-primary">
                driver nästa år framåt
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Vi ser bokslutet som starten på nästa räkenskapsår — inte slutet
              på det förra. Det förändrar hur vi arbetar med dig.
            </p>
          </div>

          <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
            {why.map((w) => (
              <div key={w.title} className="flex gap-5">
                <div className="flex-shrink-0 mt-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground font-display">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                    {w.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── TESTIMONIAL ───────── */}
      <section className="py-20 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-10">
          <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-primary-glow blur-3xl" />
        </div>
        <div className="container-page relative">
          <div className="max-w-3xl mx-auto text-center">
            <Quote
              className="h-12 w-12 text-primary mx-auto mb-8"
              strokeWidth={1.5}
            />
            <blockquote className="font-display text-2xl md:text-3xl leading-relaxed text-background/90 italic text-balance">
              Vi var i behov av kvalificerad hjälp till vår ekonomiavdelning
              inför vårt bokslutsarbete. Cloud Redovisning kunde snabbt sätta
              sig in i verksamheten och deras arbete var en viktig del i
              genomförandet av våra bokslut. Vi sätter stort värde på deras
              kunskaper, professionalism och rådgivning.
            </blockquote>
            <div className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-background/60">
              <span className="h-px w-8 bg-primary" />
              Stefan Johansson — Kaj Johansson Åkeri AB
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="grid gap-12 lg:gap-16 lg:grid-cols-12 items-center">
            <div className="lg:col-span-4">
              <div className="eyebrow">— Vanliga frågor</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Frågor om{" "}
                <span className="italic text-primary">
                  bokslut & deklaration
                </span>
                .
              </h2>
              <p className="lead mt-6">
                Det vi hör mest från företagare — med raka svar.
              </p>
              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft">
                <img
                  src={faqImage}
                  alt="Vanliga frågor om bokslut och årsredovisning"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-foreground/5 rounded-3xl" />
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="space-y-3">
                {faqs.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div
                      key={f.q}
                      className={`rounded-2xl border transition-all duration-300 ${
                        open
                          ? "border-primary/30 bg-card shadow-soft"
                          : "border-border bg-card hover:border-border-strong"
                      }`}
                    >
                      <button
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        aria-expanded={open}
                        className="w-full flex items-center justify-between gap-6 text-left p-6 md:p-7"
                      >
                        <span className="font-display text-[1.5rem] tracking-[0.005em] text-foreground">
                          {f.q}
                        </span>
                        <span
                          className={`h-9 w-9 flex-shrink-0 rounded-full border flex items-center justify-center transition-all ${
                            open
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-foreground"
                          }`}
                        >
                          {open ? (
                            <Minus className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                      <div hidden={!open} className="px-6 md:px-7 pb-7 -mt-2 text-sm text-foreground-muted leading-relaxed animate-fade-in">
                          {f.a}
                        </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-accent p-10 md:p-16 text-primary-foreground shadow-elevated">
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="relative grid gap-10 md:grid-cols-12 items-center">
              <div className="md:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-primary-foreground/70">
                  — Nästa steg
                </div>
                <h2 className="display-md mt-6 text-primary-foreground text-balance">
                  Dags att lägga bokslutet i{" "}
                  <span className="italic">trygga händer?</span>
                </h2>
                <p className="mt-6 max-w-xl text-primary-foreground/85 leading-relaxed">
                  Boka ett kostnadsfritt möte. Vi går igenom ditt bolag,
                  diskuterar skatteplanering och föreslår ett upplägg som
                  faktiskt hjälper dig framåt.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-background px-7 py-3.5 text-sm font-medium text-foreground transition-all hover:bg-foreground hover:text-background"
                >
                  Boka kostnadsfritt möte <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:0703036345"
                  className="inline-flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground"
                >
                  <Phone className="h-3.5 w-3.5" /> 070-303 63 45
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinksSection
        title="Knyt ihop bokslut, deklaration och löpande ekonomi."
        description="När bokslutsarbetet fungerar bäst finns ofta också tydliga processer för bokföring, lön och rådgivning under resten av året."
        links={[
          {
            to: "/bokforing",
            title: "Löpande bokföring",
            body: "Digital bokföring som gör bokslutsprocessen snabbare, renare och mer förutsägbar.",
          },
          {
            to: "/radgivning",
            title: "Rådgivning & 3:12",
            body: "För dig som vill arbeta strategiskt med resultat, utdelning, K10 och ägaruttag.",
          },
          {
            to: "/kontakt-umea",
            title: "Kontakt Umeå",
            body: "Prata bokslut och deklaration med vårt team i Umeå och för företag i Vännäsregionen.",
          },
          {
            to: "/kontakt-stockholm",
            title: "Kontakt Stockholm",
            body: "Boka ett första möte med Stockholmskontoret om ni vill ha stöd lokalt i regionen.",
          },
        ]}
      />

      {/* ───────── CONTACT FORM ───────── */}
      <ContactForm
        title="Kontakta oss för kostnadsfri rådgivning"
        subtitle="Skicka ett meddelande så återkommer vi snabbt. Vi tror på personlig service — inte långa väntetider."
      />
    </>
  );
};

export default Bokslut;
